import { ButtonGroup, Classes, Colors, Dialog, Icon, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import { DogHealthSearch, DogHealthTestApi, DogHealthTestGridDto } from "../../../../api";
import { AddButton } from "../../../../components";
import { useEventsContext, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalDataTable } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { DrupalDogHealthTestPanel } from "./DrupalDogHealthTestPanel";
import { DrupalDogTransientCharacteristicList } from "./DrupalDogTransientCharacteristicList";

export interface IDrupalDogCharacteristicListProps {
  dogId: number;
  refresh: () => void;
}

export const DrupalDogCharacteristicList: React.FunctionComponent<IDrupalDogCharacteristicListProps> = ({
  dogId,
  refresh,
}) => {
  const { t } = useTl();
  const api = useApiService(DogHealthTestApi);
  const { dispatchEvent } = useEventsContext();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const { currentLanguage } = useGlobalData();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
  });

  const searchFunction = React.useCallback(
    (sObj: DogHealthSearch) => {
      return api.baseSearch(
        DogHealthSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
          showInvalid: false,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "value",
        render: (row: DogHealthTestGridDto) => <div>{row.value ?? row.numericValue}</div>,
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.TestType),
        fieldName: "test",
        render: (row: DogHealthTestGridDto) => {
          if (row.characteristicType.includes("03")) {
            return <Icon icon={IconNames.EYE_OPEN} />;
          } else if (row.characteristicType.includes("04")) {
            return <Icon icon={IconNames.HEART} />;
          } else if (row.characteristicType.includes("09")) {
            return <Icon icon={IconNames.CHART} />;
          }
        },
        disableSorting: true,
      },
    ],
    [t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <DrupalFieldSet
        title={t(ETLCodes.HealthTests)}
        rightElement={
          <ButtonGroup>
            <AddButton
              onClick={(e) => {
                e.stopPropagation();
                addItemFunction();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
              secured={false}
            />
          </ButtonGroup>
        }
      >
        <DrupalDataTable tableState={tableState} loading={loading} columns={columns} />
      </DrupalFieldSet>
      <DrupalDogTransientCharacteristicList dogId={dogId} />

      <Dialog
        title={t(ETLCodes.Characteristics)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 300, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 300 }}>
          <DrupalDogHealthTestPanel
            dogId={dogId}
            onSaved={() => {
              setDialogOpened(false);
              search();
              dispatchEvent("RefreshSearchTables");
              refresh();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
